import { Box, Typography } from "@mui/material";
import { Suspense } from "react";
import ReactMarkdown from "react-markdown";

import { SCENES } from "../scenes";
import type { WidgetOfType } from "../schema/widgets";
import { colors, typeScale } from "../theme/tokens";
import { CustomerLogoGrid } from "./CustomerLogos";
import { answerMarkdownComponents } from "./FAQ";
import type { WidgetProps } from "./types";

// The public website's What-we-do feature rows. Each row is copy on one side
// and a vendored site scene (animated SVG) on the other, alternating sides
// exactly like components/sections/FeatureRow.tsx on the site. Content is
// canonical (templates/capabilities-canonical.ts); this file only lays it
// out.
//
// The Capabilities page renders one section at a time behind its own
// contents rail (templates/CapabilitiesOverview.tsx, Bill 2026-09-07): it
// draws the section's title row itself and uses `FeatureSectionRows` for the
// rows. The plain widget below stacks every section with `FeatureSection`
// and exists only so a `feature_rows` widget still renders on any other page.
//
// Since 2026-09-08 (the How We Partner page) a row may carry no scene: it
// then renders as a single text column at the reading measure, and a
// section may carry no title. Row bodies are markdown rendered with the
// FAQ's link handling, so a row can point at another page in the room.
//
// Since 2026-10-26 the rows follow the brand's "Numbered feature rows"
// pattern (Bison-brand v1.0.0, the site's components/service/FeatureList):
// the small orange 01, 02, 03 over each title, the title at the section
// scale, the body at the lead scale, and a hairline between rows. Row
// spacing stays at half the site's (Bill, 2026-09-08), now half of the
// site's current py-16 / py-20.
type Section = WidgetOfType<"feature_rows">["props"]["sections"][number];

const bodyMarkdownComponents = {
    ...answerMarkdownComponents,
    p: ({ children }: { children?: React.ReactNode }) => (
        <Typography variant="body1" sx={{ color: "text.secondary", ...typeScale.lead, mt: 2 }}>
            {children}
        </Typography>
    ),
};

export function FeatureRows({ widget }: WidgetProps<"feature_rows">) {
    return (
        <Box>
            {widget.props.sections.map((section, k) => {
                // A rule between sections; the first gets one only on request, to set it
                // apart from the widget above (exclusivity pitch room, 2026-10-27).
                const ruled = k > 0 || section.divider;
                return (
                    <Box
                        key={section.id}
                        sx={{
                            pt: ruled ? { xs: 5, md: 7 } : 0,
                            mt: ruled ? { xs: 5, md: 7 } : 0,
                            borderTop: ruled ? 1 : 0,
                            borderColor: colors.line,
                        }}
                    >
                        <FeatureSection section={section} />
                    </Box>
                );
            })}
        </Box>
    );
}

export function FeatureSection({ section }: { section: Section }) {
    return (
        <Box>
            {section.eyebrow && (
                <Typography variant="overline" component="p" sx={{ mb: 0.5 }}>
                    {section.eyebrow}
                </Typography>
            )}
            {section.title && (
                <Typography variant="h2" sx={{ mt: 0, mb: section.intro ? 1.5 : 0 }}>
                    {section.title}
                </Typography>
            )}
            {section.intro && (
                <Typography
                    variant="body1"
                    sx={{ color: "text.secondary", maxWidth: 720, ...typeScale.lead }}
                >
                    {section.intro}
                </Typography>
            )}

            {/* Under a section title of their own, the row titles step down a
                level and a size. */}
            <FeatureSectionRows section={section} rowHeading={section.title ? "h3" : "h2"} />
        </Box>
    );
}

export function FeatureSectionRows({
    section,
    rowHeading = "h2",
}: {
    section: Section;
    rowHeading?: "h2" | "h3";
}) {
    // Sides alternate over the rows that have a scene, so a text-only row
    // between two scene rows does not put both scenes on the same side.
    const sides: ("left" | "right")[] = [];
    for (let i = 0, scenes = 0; i < section.rows.length; i += 1) {
        const row = section.rows[i];
        sides.push(row.mediaSide ?? (scenes % 2 === 0 ? "right" : "left"));
        if (row.scene || row.image || row.logos) scenes += 1;
    }
    return (
        <>
            {section.rows.map((row, i) => (
                <FeatureRow
                    key={row.id}
                    index={i}
                    heading={rowHeading}
                    title={row.title}
                    body={row.body}
                    scene={row.scene}
                    sceneScale={row.sceneScale}
                    scenePadY={row.scenePadY}
                    image={row.image}
                    images={row.images}
                    logos={row.logos}
                    mediaSide={sides[i]}
                    alignTop={section.alignTop}
                />
            ))}
        </>
    );
}

function FeatureRow({
    index,
    heading,
    title,
    body,
    scene,
    sceneScale = 1,
    scenePadY = 0,
    image,
    images,
    logos,
    mediaSide,
    alignTop,
}: {
    index: number;
    heading: "h2" | "h3";
    title: string;
    body: string;
    scene?: keyof typeof SCENES;
    sceneScale?: number;
    scenePadY?: number;
    image?: { src: string; alt: string };
    images?: { src: string; alt: string }[];
    logos?: Section["rows"][number]["logos"];
    mediaSide: "left" | "right";
    alignTop?: boolean;
}) {
    const copy = (
        <>
            {/* Numbered label: 14px, 600, tabular, orange. It counts; it is
                decorative, so screen readers skip it. */}
            <Typography
                aria-hidden
                sx={{
                    fontSize: "0.875rem",
                    fontWeight: 600,
                    fontVariantNumeric: "tabular-nums",
                    color: colors.brandOrange,
                    mb: 1.5,
                    userSelect: "none",
                }}
            >
                {String(index + 1).padStart(2, "0")}
            </Typography>
            <Typography variant={heading} sx={{ mt: 0, textWrap: "balance" }}>
                {title}
            </Typography>
            <ReactMarkdown components={bodyMarkdownComponents}>{body}</ReactMarkdown>
        </>
    );

    // Half the site's py-16 / md:py-20, with the hairline between rows.
    const rowSx = {
        py: { xs: 4, md: 5 },
        borderTop: index > 0 ? 1 : 0,
        borderColor: colors.line,
    } as const;

    if (!scene && !image && !images && !logos) {
        return (
            <Box sx={rowSx}>
                <Box sx={{ maxWidth: 1064 }}>{copy}</Box>
            </Box>
        );
    }

    // The site's photo rows (Bill, 2026-09-08: the rows with stock imagery
    // were being dropped): the photo in a 1px-line card at 16:10, cover-fit.
    const media = logos ? (
        <CustomerLogoGrid customers={logos} />
    ) : images ? (
        <Slideshow images={images} />
    ) : image ? (
        <Box
            component="img"
            src={image.src}
            alt={image.alt}
            loading="lazy"
            sx={{
                display: "block",
                width: "100%",
                aspectRatio: "16 / 10",
                objectFit: "cover",
                borderRadius: "10px",
                border: 1,
                borderColor: colors.line,
                bgcolor: "background.paper",
            }}
        />
    ) : null;

    const Scene = scene && !images ? SCENES[scene] : null;
    return (
        <Box
            sx={{
                ...rowSx,
                display: "grid",
                // Copy level with the scene's top on request (exclusivity pitch room).
                alignItems: alignTop ? "start" : "center",
                // The site's gap-10 / lg:gap-14.
                gap: { xs: 5, md: 7 },
                // Same 0.9 / 1.1 split as the site's FeatureRow; the media
                // column is the wider one whichever side it sits on.
                gridTemplateColumns: {
                    xs: "1fr",
                    md: mediaSide === "left" ? "1.1fr 0.9fr" : "0.9fr 1.1fr",
                },
            }}
        >
            <Box sx={{ order: { xs: 0, md: mediaSide === "left" ? 2 : 1 }, minWidth: 0 }}>
                {copy}
            </Box>
            <Box
                className="dr-scene"
                sx={{
                    order: { xs: 1, md: mediaSide === "left" ? 1 : 2 },
                    width: "100%",
                    maxWidth: Math.round(600 * sceneScale),
                    py: `${scenePadY}px`,
                    mx: "auto",
                    minWidth: 0,
                }}
            >
                {media}
                {Scene && (
                    <Suspense
                        fallback={
                            <Box
                                sx={{
                                    aspectRatio: "4 / 3",
                                    borderRadius: "10px",
                                    bgcolor: colors.lightGray,
                                }}
                            />
                        }
                    >
                        <Scene className="w-full" />
                    </Suspense>
                )}
            </Box>
        </Box>
    );
}

// A crossfade of stills, one every few seconds (the supplier's own product
// pages on How We Partner: "choose three or four product screenshots and
// stitch it together into an animation", Bill 2026-09-08). Each still is a
// 1px-line card at 4:3 with the whole still shown (contain, pinned to the
// top: a cover fit cut the Amazon page), and the fade is CSS only so reduce-motion turns it
// into a fixed first frame.
function Slideshow({ images }: { images: { src: string; alt: string }[] }) {
    const period = 4;
    const total = images.length * period;
    return (
        <Box
            className="dr-scene"
            sx={{
                position: "relative",
                width: "100%",
                aspectRatio: "4 / 3",
                borderRadius: "10px",
                border: 1,
                borderColor: colors.line,
                bgcolor: "background.paper",
                overflow: "hidden",
                "@keyframes dr-slide": {
                    "0%": { opacity: 0 },
                    "6%": { opacity: 1 },
                    [`${(100 / images.length).toFixed(2)}%`]: { opacity: 1 },
                    [`${(100 / images.length + 6).toFixed(2)}%`]: { opacity: 0 },
                    "100%": { opacity: 0 },
                },
            }}
        >
            {images.map((img, i) => (
                <Box
                    key={img.src}
                    component="img"
                    src={img.src}
                    alt={img.alt}
                    loading="lazy"
                    sx={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        objectPosition: "top",
                        opacity: i === 0 ? 1 : 0,
                        animation:
                            images.length > 1 ? `dr-slide ${total}s linear infinite` : "none",
                        animationDelay: `${i * period}s`,
                    }}
                />
            ))}
        </Box>
    );
}
